import { Box, List, ListItemButton, ListItemText, Typography, useTheme } from '@mui/material';
import React from 'react';

import { ALL_GROUPS, GroupSummary } from './columnSelectionHelpers';

export interface ColumnSelectionSidebarProps {
  summaries: Array<GroupSummary>;
  activeGroup: string;
  totals: { selected: number; total: number };
  /** True when a search is on, so the counts read as matches rather than the full group. */
  isSearching: boolean;
  onSelect: (group: string) => void;
}

export const ColumnSelectionSidebar: React.FC<ColumnSelectionSidebarProps> = ({
  summaries,
  activeGroup,
  totals,
  isSearching,
  onSelect,
}) => {
  const theme = useTheme();

  const rows = [
    {
      key: ALL_GROUPS,
      title: 'All columns',
      selected: isSearching
        ? summaries.reduce((sum, summary) => sum + summary.selected, 0)
        : totals.selected,
      total: isSearching
        ? summaries.reduce((sum, summary) => sum + summary.total, 0)
        : totals.total,
    },
    ...summaries.map((summary) => ({ key: summary.title, ...summary })),
  ];

  return (
    <Box
      component="nav"
      aria-label="Column groups"
      sx={{
        width: 176,
        flexShrink: 0,
        borderRight: `1px solid ${theme.palette.v4.border}`,
        overflowY: 'auto',
      }}
    >
      <List dense disablePadding sx={{ py: 1 }}>
        {rows.map((row) => {
          const isActive = row.key === activeGroup;
          return (
            <ListItemButton
              key={row.key}
              selected={isActive}
              aria-current={isActive ? 'true' : undefined}
              onClick={() => onSelect(row.key)}
              aria-label={`${row.title}, ${row.selected} of ${row.total} ${
                isSearching ? 'matching columns' : 'columns'
              } selected`}
              sx={{
                py: 0.75,
                px: 1.5,
                borderLeft: isActive
                  ? `3px solid ${theme.palette.v4.accent}`
                  : '3px solid transparent',
                '&.Mui-selected': {
                  bgcolor: 'transparent',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                },
              }}
            >
              <ListItemText
                primary={row.title}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    fontWeight: isActive ? 600 : 400,
                    noWrap: true,
                  },
                }}
              />
              <Typography
                aria-hidden
                variant="caption"
                sx={{ color: theme.palette.text.secondary, ml: 0.5, whiteSpace: 'nowrap' }}
              >
                {row.selected}/{row.total}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};
